// libraries
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// consts/enums
import { CLEAR_AIO_DXG_EVENT_ERRORS } from '../actionTypes/baseDealActionTypes';
import { AIO_DXG_EVENTS } from '../actionTypes/socketActionTypes';
import { FS_SYNC_FINAL_ATTEMPT_TIMEOUT_MS } from '../constants';

// actions
import { dismissFsSyncFailure, fsSyncFinalAttemptTimedOut } from '../actions/baseDealActionCreators';

// selectors
import { getFsCoxAutoCorrelationId, getFsRetryState, getFsSyncFailureViewModel } from '../selectors/fsSyncFailureSelectors';
import { getDealXgId } from '../selectors/offerRedux';

const FS_SYNC_FINAL_ATTEMPT_TIMEOUT_PAGE_ACTION = 'MV:FsSyncFinalAttemptTimeout';

type PendingFinalAttemptWait = {
    timeoutId: ReturnType<typeof setTimeout>;
    startedAt: number;
};

const pendingFinalAttemptWaits = new Map<string, PendingFinalAttemptWait>();

/**
 * True only while a retry is genuinely outstanding. Used both to start the wait and to re-check on expiry, so a
 * late-but-real final attempt cannot be contradicted by a timer that was already in flight.
 */
const isAwaitingFinalAttempt = (state: StateTree): boolean => {
    const { attempt, maxAttempts } = getFsRetryState(state);

    return (
        getFsSyncFailureViewModel(state).category === 'transient' &&
        attempt !== undefined &&
        maxAttempts !== undefined &&
        attempt < maxAttempts
    );
};

const clearFinalAttemptWait = (dealXgId: string) => {
    const pending = pendingFinalAttemptWaits.get(dealXgId);

    if (pending) {
        clearTimeout(pending.timeoutId);
        pendingFinalAttemptWaits.delete(dealXgId);
    }
};

const beginFinalAttemptWait = (store: ReduxStore<StateTree>, dealXgId: string) => {
    clearFinalAttemptWait(dealXgId);

    const startedAt = Date.now();

    const timeoutId = setTimeout(() => {
        pendingFinalAttemptWaits.delete(dealXgId);

        const state = store.getState();

        if (!isAwaitingFinalAttempt(state)) {
            return;
        }

        const { attempt, maxAttempts } = getFsRetryState(state);

        store.dispatch(fsSyncFinalAttemptTimedOut());
        logNewRelicPageAction(FS_SYNC_FINAL_ATTEMPT_TIMEOUT_PAGE_ACTION, {
            dealXgId,
            coxAutoCorrelationId: getFsCoxAutoCorrelationId(state),
            lastAttemptSeen: attempt,
            maxAttempts,
            elapsedMs: Date.now() - startedAt,
            timeoutMs: FS_SYNC_FINAL_ATTEMPT_TIMEOUT_MS
        });
    }, FS_SYNC_FINAL_ATTEMPT_TIMEOUT_MS);

    pendingFinalAttemptWaits.set(dealXgId, { timeoutId, startedAt });
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case AIO_DXG_EVENTS: {
            if (
                action.payload?.type === 'fsSuccess' &&
                !action.payload.fsNoOp &&
                getFsSyncFailureViewModel(store.getState()).category !== undefined
            ) {
                store.dispatch(dismissFsSyncFailure());
            }

            const state = store.getState();
            const dealXgId = getDealXgId(state);

            if (!dealXgId) {
                break;
            }

            if (isAwaitingFinalAttempt(state)) {
                beginFinalAttemptWait(store, dealXgId);
            } else {
                clearFinalAttemptWait(dealXgId);
            }

            break;
        }
        case CLEAR_AIO_DXG_EVENT_ERRORS: {
            clearFinalAttemptWait(getDealXgId(store.getState()));
            break;
        }
        default:
            break;
    }
};
